import { type FC, type ReactNode, useRef } from "react";
import { useFocusable } from "react-aria";
import { tv, type VariantProps } from "tailwind-variants";

export const hoverTargetStyle = tv({
  // Shrink-wrapped and inline, so the box matches the content rather than the
  // container: that keeps the focus ring around what the user is pointing at,
  // and gives the tooltip an anchor over it instead of over the middle of the
  // surrounding cell.
  //
  // To ellipsize, pass `block truncate` — `text-overflow` only applies to a
  // block box, so `truncate` on its own silently hard-clips instead. `w-fit`
  // survives the override, so short text still shrink-wraps and only text that
  // runs out of room caps at the container.
  base: "inline-flex w-fit max-w-full cursor-[inherit] items-center focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
  variants: {
    /**
     * Match the corner radius of the content, so the focus ring traces its
     * edge instead of cutting across it. A `Pill`, a `Button` and a card are
     * all `rounded-md`, which is why there is no separate variant per widget.
     */
    shape: {
      /** Text, bars, timestamps — anything squared off. */
      square: "rounded-xs",
      /** Pills, buttons, cards, and bare icons. */
      rounded: "rounded-md",
      /**
       * Only content that is itself a circle, like a status dot. A bare icon
       * takes `rounded` — `InfoButton` is round because it's a button with a
       * circular hit area, not because it holds an icon.
       */
      circle: "rounded-full",
    },
  },
  defaultVariants: {
    shape: "square",
  },
});

export interface TooltipHoverTargetProps extends VariantProps<
  typeof hoverTargetStyle
> {
  className?: string;
  isDisabled?: boolean;
  children?: ReactNode;
}

/**
 * Turns any non-clickable element into a tooltip target for use in `TooltipTrigger`.
 *
 * Normally only interactive elements can have tooltips. Where you must put one
 * on something non-interactive, use this as a wrapper to make it hoverable and
 * focusable. Set `shape` to match what it wraps — the wrapper is what takes the
 * focus ring, so a square ring around a pill is the giveaway that it's unset.
 */
export const TooltipHoverTarget: FC<TooltipHoverTargetProps> = (props) => {
  const focusableRef = useRef<HTMLSpanElement>(null);

  // We only want focus behavior.
  const { focusableProps } = useFocusable(props, focusableRef);

  return (
    // A span rather than a div: this wraps arbitrary content in arbitrary
    // places, and a div inside a `<p>` is a parse error that breaks hydration.
    <span
      ref={focusableRef}
      {...focusableProps}
      // This is a lie to silence a warning, since this isn't clickable. Pressing this may bubble up to an ancestor, though.
      role="button"
      className={hoverTargetStyle({
        shape: props.shape,
        className: props.className,
      })}
    >
      {props.children}
    </span>
  );
};
